import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { allGenres } from '@/utils/movie';
import SearchBar from '@/components/feature/SearchBar';

const navLinks = [
  { to: '/', label: '首页' },
  { to: '/about', label: '关于我们' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [genreOpen, setGenreOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setGenreOpen(false);
  }, [location.pathname, location.search]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || menuOpen
          ? 'border-b border-background-300/60 bg-background-50/95 backdrop-blur-md'
          : 'border-b border-transparent bg-gradient-to-b from-background-50/90 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4 md:h-[72px]">
          <Link to="/" className="group flex items-center gap-2.5 shrink-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-lg text-foreground-950 transition-transform group-hover:scale-105">
              <i className="ri-film-fill" aria-hidden="true" />
            </span>
            <span className="font-heading text-lg font-extrabold tracking-tight text-foreground-50 md:text-xl">
              免费看大片
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navLinks.slice(0, 1).map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? 'text-primary-400' : 'text-foreground-200 hover:text-foreground-50'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}

            <div
              className="relative"
              onMouseEnter={() => setGenreOpen(true)}
              onMouseLeave={() => setGenreOpen(false)}
            >
              <button
                type="button"
                onClick={() => setGenreOpen((v) => !v)}
                className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-foreground-200 transition-colors hover:text-foreground-50 cursor-pointer"
                aria-haspopup="true"
                aria-expanded={genreOpen}
              >
                电影分类
                <i className={`ri-arrow-down-s-line transition-transform ${genreOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {genreOpen && (
                <div className="absolute left-0 top-full w-[440px] pt-2">
                  <div className="grid grid-cols-3 gap-1 rounded-lg border border-background-300/70 bg-background-100 p-2">
                    {allGenres.map((g) => (
                      <Link
                        key={g.key}
                        to={`/category/${g.key}`}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-200 transition-colors hover:bg-background-200 hover:text-primary-400"
                      >
                        <i className={`${g.icon} text-primary-400`} aria-hidden="true" />
                        {g.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {navLinks.slice(1).map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? 'text-primary-400' : 'text-foreground-200 hover:text-foreground-50'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden w-72 lg:block xl:w-80">
            <SearchBar />
          </div>

          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-md text-2xl text-foreground-100 md:hidden cursor-pointer"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? '关闭菜单' : '打开菜单'}
          >
            <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'} aria-hidden="true" />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-background-300/60 bg-background-50 px-4 pb-6 pt-4 md:hidden">
          <SearchBar />
          <div className="mt-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? 'bg-background-200 text-primary-400' : 'text-foreground-200'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>
          <p className="mb-2 mt-4 px-3 text-xs font-semibold uppercase tracking-wider text-foreground-500">
            电影分类
          </p>
          <div className="grid grid-cols-2 gap-1">
            {allGenres.map((g) => (
              <Link
                key={g.key}
                to={`/category/${g.key}`}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-200 transition-colors hover:bg-background-200"
              >
                <i className={`${g.icon} text-primary-400`} aria-hidden="true" />
                {g.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}